/**
 * Inspector section (nothing selected → Canvas filters): the dashboard's
 * canvas-level filters. Each one picks an entity, a column of it, an operator
 * and a value, and the widgets and texts it filters - only those, not the
 * whole dashboard. Written to `canvas.filters`; the runtime applies them to
 * every request that loads a target (see `design-canvas-filter.utils.ts`).
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';

import { SsSelectTooltipDirective } from '../../../shared/ss-select-tooltip.directive';
import type { CanvasFilter, DesignNode } from '../../design-builder.types';
import { DesignDataService } from '../../services/design-data.service';
import { DesignDocumentStore } from '../../state/design-document.store';
import { flattenNodes } from '../../utils/design-node.utils';
import {
  CANVAS_FILTER_OPERATORS,
  createCanvasFilter,
  isCanvasFilterActive,
  isValuelessOperator,
} from '../../utils/design-canvas-filter.utils';

/** One pickable target: a widget or a text layer, by node id. */
interface CanvasFilterTarget {
  value: string;
  label: string;
  group: 'Components' | 'Texts';
}

@Component({
  selector: 'app-canvas-filters',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './canvas-filters.component.html',
  styleUrl: './canvas-filters.component.scss',
})
export class CanvasFiltersComponent {
  private readonly doc = inject(DesignDocumentStore);
  protected readonly data = inject(DesignDataService);

  readonly operators = CANVAS_FILTER_OPERATORS;
  readonly filters = computed<CanvasFilter[]>(() => this.doc.canvas().filters ?? []);

  readonly datasets = computed<string[]>(() => {
    const state = this.data.datasets();
    return state.status === 'ready' ? state.value : [];
  });

  /** Every widget and text layer on every page: what a filter can target. */
  readonly targets = computed<CanvasFilterTarget[]>(() => {
    const nodes = flattenNodes(this.doc.document().pages.flatMap((page) => page.nodes));
    const widgets = nodes.filter((node) => node.type === 'WIDGET' && !!node.widget).map((node): CanvasFilterTarget => ({
      value: node.id,
      label: `${nodeName(node)}${hasComponent(node) ? '' : ' (not generated)'}`,
      group: 'Components',
    }));
    const texts = nodes.filter((node) => node.type === 'TEXT').map((node): CanvasFilterTarget => ({
      value: node.id,
      label: `${nodeName(node)}${node.dataBinding?.componentId ? '' : ' (not bound to data)'}`,
      group: 'Texts',
    }));
    return [...widgets, ...texts];
  });

  constructor() {
    this.data.loadDatasets();
    // The column lists of the entities already picked.
    effect(() => {
      const entities = [...new Set(this.filters().map((filter) => filter.entity).filter(Boolean))];
      untracked(() => entities.forEach((entity) => this.data.loadColumns(entity)));
    });
  }

  columns(entity: string): string[] {
    const state = entity ? this.data.columns(entity) : null;
    return state?.status === 'ready' ? state.value : [];
  }

  columnsLoading(entity: string): boolean {
    return !!entity && this.data.columns(entity).status === 'loading';
  }

  needsValue(filter: CanvasFilter): boolean {
    return !isValuelessOperator(filter.operator);
  }

  isList(filter: CanvasFilter): boolean {
    return (filter.operator || '').toLowerCase() === 'in';
  }

  valueText(filter: CanvasFilter): string {
    return (filter.values ?? []).join(', ');
  }

  /** Why a filter does not apply yet, or '' when it does. */
  problem(filter: CanvasFilter): string {
    if (filter.disabled) return 'Turned off - not applied.';
    if (isCanvasFilterActive(filter)) return '';
    if (!filter.entity) return 'Pick an entity.';
    if (!filter.field) return 'Pick the filter column.';
    if (this.needsValue(filter) && !filter.values?.length) return 'Enter a value.';
    if (!filter.targets?.length) return 'Pick the components / texts to filter.';
    return '';
  }

  add(): void {
    this.write([...this.filters(), createCanvasFilter()]);
  }

  remove(id: string): void {
    this.write(this.filters().filter((filter) => filter.id !== id));
  }

  setEntity(id: string, entity: string | null): void {
    const value = entity ?? '';
    if (value) this.data.loadColumns(value);
    // Another entity's columns differ: the column is picked again.
    this.update(id, { entity: value, field: '' });
  }

  setField(id: string, field: string | null): void {
    this.update(id, { field: field ?? '' });
  }

  setOperator(id: string, operator: string | null, filter: CanvasFilter): void {
    const next = operator || 'eq';
    this.update(id, { operator: next, values: isValuelessOperator(next) ? [] : (filter.values ?? []) });
  }

  /** `in` takes a comma-separated list; the other operators one value. */
  setValue(id: string, raw: unknown, filter: CanvasFilter): void {
    const text = String(raw ?? '');
    const values = this.isList(filter)
      ? text.split(',').map((value) => value.trim()).filter(Boolean)
      : (text.trim() ? [text.trim()] : []);
    this.update(id, { values });
  }

  setTargets(id: string, targets: string[] | null): void {
    this.update(id, { targets: [...new Set(targets ?? [])] });
  }

  setEnabled(id: string, enabled: boolean): void {
    this.update(id, { disabled: enabled ? undefined : true });
  }

  trackById(_index: number, filter: CanvasFilter): string {
    return filter.id;
  }

  private update(id: string, patch: Partial<CanvasFilter>): void {
    this.write(this.filters().map((filter) => {
      if (filter.id !== id) return filter;
      const next: CanvasFilter = { ...filter, ...patch };
      if (!next.disabled) delete next.disabled;
      return next;
    }));
  }

  private write(filters: CanvasFilter[]): void {
    this.doc.patchCanvas({ filters: filters.length ? filters : undefined });
  }
}

function nodeName(node: DesignNode): string {
  return node.name?.trim() || node.widget?.layoutType || node.type;
}

/** A widget that loads data: generated (a component id), or an input box filled from data. */
function hasComponent(node: DesignNode): boolean {
  return !!(node.widget?.componentId || node.widget?.build?.['componentId'] || node.widget?.binding?.['dataset']);
}
